import type { CurvaDocument } from "@/document/model/document";
import { getAllSceneNodes } from "@/document/model/document";
import { sampleEdgeBezier } from "@/document/operations/vectorGeometry";
import { nodeSelectionPointToLocal, nodeSelectionPointToWorld, type ScenePreview } from "@/document/operations/nodeWorldTransform";
import type { Rect } from "@/lib/math/rect";
import type { ActiveTool } from "@/editor/shell/TopBar";
import { pointInPolygon } from "./shapeHitTest";
import { normalizeRect } from "./hitTest";

export interface VectorEdgeHit {
  edgeId: string;
  from: { x: number; y: number };
  to: { x: number; y: number };
  fromHandle?: { x: number; y: number };
  toHandle?: { x: number; y: number };
  point: { x: number; y: number };
  t: number;
}

export interface VectorIntersectionHit {
  point: { x: number; y: number };
  edges: [
    { edgeId: string; t: number },
    { edgeId: string; t: number },
  ];
}

export function hitTestVectorVertex(
  document: CurvaDocument,
  nodeId: string,
  point: { x: number; y: number },
  zoom: number,
  previews: ScenePreview[] = [],
) {
  const node = getAllSceneNodes(document).find(candidate => candidate.id === nodeId);
  if (node?.type !== "vectorNetwork") {
    return undefined;
  }

  const radius = 10 / zoom;
  const localPoint = nodeSelectionPointToLocal(document, nodeId, point, previews);
  return [...node.network.vertices].reverse().find(vertex => Math.hypot(vertex.x - localPoint.x, vertex.y - localPoint.y) <= radius);
}

export function getVectorVerticesInRect(
  document: CurvaDocument,
  nodeId: string,
  rect: Rect,
  previews: ScenePreview[] = [],
): string[] {
  const node = getAllSceneNodes(document).find(candidate => candidate.id === nodeId);
  if (node?.type !== "vectorNetwork") {
    return [];
  }

  const normalized = normalizeRect(rect);
  return node.network.vertices
    .filter(vertex => {
      const worldVertex = nodeSelectionPointToWorld(document, nodeId, vertex, previews);
      return (
        worldVertex.x >= normalized.x &&
        worldVertex.x <= normalized.x + normalized.width &&
        worldVertex.y >= normalized.y &&
        worldVertex.y <= normalized.y + normalized.height
      );
    })
    .map(vertex => vertex.id);
}

export function getVectorVerticesInLasso(
  document: CurvaDocument,
  nodeId: string,
  lasso: Array<{ x: number; y: number }>,
  previews: ScenePreview[] = [],
): string[] {
  const node = getAllSceneNodes(document).find(candidate => candidate.id === nodeId);
  if (node?.type !== "vectorNetwork" || lasso.length < 3) {
    return [];
  }

  const localLasso = lasso.map(point => nodeSelectionPointToLocal(document, nodeId, point, previews));
  return node.network.vertices
    .filter(vertex => pointInPolygon(vertex, localLasso))
    .map(vertex => vertex.id);
}

export function getVectorEdgesInRect(
  document: CurvaDocument,
  nodeId: string,
  rect: Rect,
  previews: ScenePreview[] = [],
): string[] {
  const node = getAllSceneNodes(document).find(candidate => candidate.id === nodeId);
  if (node?.type !== "vectorNetwork") {
    return [];
  }

  const normalized = normalizeRect(rect);
  return node.network.edges
    .filter(edge => {
      const from = node.network.vertices.find(vertex => vertex.id === edge.from.vertexId);
      const to = node.network.vertices.find(vertex => vertex.id === edge.to.vertexId);
      if (!from || !to) {
        return false;
      }
      return edgeSamplesForAreaSelection(from, to, edge.from.handle, edge.to.handle).some(sample => {
        const world = nodeSelectionPointToWorld(document, nodeId, sample, previews);
        return (
          world.x >= normalized.x &&
          world.x <= normalized.x + normalized.width &&
          world.y >= normalized.y &&
          world.y <= normalized.y + normalized.height
        );
      });
    })
    .map(edge => edge.id);
}

export function getVectorEdgesInLasso(
  document: CurvaDocument,
  nodeId: string,
  lasso: Array<{ x: number; y: number }>,
  previews: ScenePreview[] = [],
): string[] {
  const node = getAllSceneNodes(document).find(candidate => candidate.id === nodeId);
  if (node?.type !== "vectorNetwork" || lasso.length < 3) {
    return [];
  }

  const localLasso = lasso.map(point => nodeSelectionPointToLocal(document, nodeId, point, previews));
  return node.network.edges
    .filter(edge => {
      const from = node.network.vertices.find(vertex => vertex.id === edge.from.vertexId);
      const to = node.network.vertices.find(vertex => vertex.id === edge.to.vertexId);
      if (!from || !to) {
        return false;
      }
      return edgeSamplesForAreaSelection(from, to, edge.from.handle, edge.to.handle)
        .some(sample => pointInPolygon(sample, localLasso));
    })
    .map(edge => edge.id);
}

/**
 * Area selection for vector edit: vertices win when any are hit; otherwise edges.
 */
export function resolveVectorAreaSelection(
  document: CurvaDocument,
  nodeId: string,
  area: { type: "rect"; rect: Rect } | { type: "lasso"; points: Array<{ x: number; y: number }> },
  previews: ScenePreview[] = [],
): { kind: "vertices" | "edges"; ids: string[] } {
  const vertexIds = area.type === "rect"
    ? getVectorVerticesInRect(document, nodeId, area.rect, previews)
    : getVectorVerticesInLasso(document, nodeId, area.points, previews);
  if (vertexIds.length > 0) {
    return { kind: "vertices", ids: vertexIds };
  }

  const edgeIds = area.type === "rect"
    ? getVectorEdgesInRect(document, nodeId, area.rect, previews)
    : getVectorEdgesInLasso(document, nodeId, area.points, previews);
  return { kind: "edges", ids: edgeIds };
}

/** Dense samples so straight edges (endpoint-only bezier samples) still hit mid-edge marquees. */
function edgeSamplesForAreaSelection(
  from: { x: number; y: number },
  to: { x: number; y: number },
  fromHandle: { x: number; y: number } | undefined,
  toHandle: { x: number; y: number } | undefined,
): Array<{ x: number; y: number }> {
  if (fromHandle || toHandle) {
    return sampleEdgeBezier(from, to, fromHandle, toHandle, 24);
  }

  const samples: Array<{ x: number; y: number }> = [];
  for (let index = 0; index <= 24; index += 1) {
    const t = index / 24;
    samples.push({
      x: from.x + (to.x - from.x) * t,
      y: from.y + (to.y - from.y) * t,
    });
  }
  return samples;
}

export function hitTestVectorHandle(
  document: CurvaDocument,
  nodeId: string,
  point: { x: number; y: number },
  zoom: number,
  previews: ScenePreview[] = [],
) {
  const node = getAllSceneNodes(document).find(candidate => candidate.id === nodeId);
  if (node?.type !== "vectorNetwork") {
    return undefined;
  }

  const radius = 9 / zoom;
  const localPoint = nodeSelectionPointToLocal(document, nodeId, point, previews);
  for (const edge of [...node.network.edges].reverse()) {
    if (edge.to.handle && Math.hypot(edge.to.handle.x - localPoint.x, edge.to.handle.y - localPoint.y) <= radius) {
      return { edgeId: edge.id, endpoint: "to" as const, x: edge.to.handle.x, y: edge.to.handle.y };
    }

    if (edge.from.handle && Math.hypot(edge.from.handle.x - localPoint.x, edge.from.handle.y - localPoint.y) <= radius) {
      return { edgeId: edge.id, endpoint: "from" as const, x: edge.from.handle.x, y: edge.from.handle.y };
    }
  }

  return undefined;
}

export function hitTestVectorEdge(
  document: CurvaDocument,
  nodeId: string,
  point: { x: number; y: number },
  zoom: number,
  previews: ScenePreview[] = [],
): VectorEdgeHit | undefined {
  const node = getAllSceneNodes(document).find(candidate => candidate.id === nodeId);
  if (node?.type !== "vectorNetwork") {
    return undefined;
  }

  const tolerance = 7 / zoom;
  const localPoint = nodeSelectionPointToLocal(document, nodeId, point, previews);
  for (const edge of [...node.network.edges].reverse()) {
    const from = node.network.vertices.find(vertex => vertex.id === edge.from.vertexId);
    const to = node.network.vertices.find(vertex => vertex.id === edge.to.vertexId);
    if (!from || !to) {
      continue;
    }

    const samples = sampleEdgeBezier(from, to, edge.from.handle, edge.to.handle, 24);
    const closest = closestPointOnPolyline(localPoint, samples);
    if (closest.distance <= tolerance) {
      return {
        edgeId: edge.id,
        from,
        to,
        fromHandle: edge.from.handle,
        toHandle: edge.to.handle,
        point: closest.point,
        t: closest.t,
      };
    }
  }

  return undefined;
}

export function hitTestVectorIntersection(
  document: CurvaDocument,
  nodeId: string,
  point: { x: number; y: number },
  zoom: number,
  previews: ScenePreview[] = [],
): VectorIntersectionHit | undefined {
  const node = getAllSceneNodes(document).find(candidate => candidate.id === nodeId);
  if (node?.type !== "vectorNetwork") {
    return undefined;
  }

  const tolerance = 10 / zoom;
  const endpointEpsilon = 0.001;
  const localPoint = nodeSelectionPointToLocal(document, nodeId, point, previews);
  const sampledEdges = node.network.edges.flatMap(edge => {
    const from = node.network.vertices.find(vertex => vertex.id === edge.from.vertexId);
    const to = node.network.vertices.find(vertex => vertex.id === edge.to.vertexId);
    if (!from || !to) {
      return [];
    }

    const samples = sampleEdgeBezier(from, to, edge.from.handle, edge.to.handle, 32);
    return [{ edge, samples, bounds: pointsBounds(samples) }];
  }).filter(({ bounds }) => pointInExpandedBounds(localPoint, bounds, tolerance));

  let closest: (VectorIntersectionHit & { distance: number }) | undefined;
  for (let firstIndex = 0; firstIndex < sampledEdges.length; firstIndex += 1) {
    const first = sampledEdges[firstIndex]!;
    for (let secondIndex = firstIndex + 1; secondIndex < sampledEdges.length; secondIndex += 1) {
      const second = sampledEdges[secondIndex]!;
      if (
        first.edge.from.vertexId === second.edge.from.vertexId ||
        first.edge.from.vertexId === second.edge.to.vertexId ||
        first.edge.to.vertexId === second.edge.from.vertexId ||
        first.edge.to.vertexId === second.edge.to.vertexId
      ) {
        continue;
      }

      for (let firstSegment = 0; firstSegment < first.samples.length - 1; firstSegment += 1) {
        for (let secondSegment = 0; secondSegment < second.samples.length - 1; secondSegment += 1) {
          if (
            !pointInExpandedSegmentBounds(localPoint, first.samples[firstSegment]!, first.samples[firstSegment + 1]!, tolerance) ||
            !pointInExpandedSegmentBounds(localPoint, second.samples[secondSegment]!, second.samples[secondSegment + 1]!, tolerance)
          ) {
            continue;
          }

          const intersection = segmentIntersection(
            first.samples[firstSegment]!,
            first.samples[firstSegment + 1]!,
            second.samples[secondSegment]!,
            second.samples[secondSegment + 1]!,
          );
          if (!intersection) {
            continue;
          }

          const firstT = (firstSegment + intersection.firstT) / Math.max(1, first.samples.length - 1);
          const secondT = (secondSegment + intersection.secondT) / Math.max(1, second.samples.length - 1);
          if (
            firstT <= endpointEpsilon ||
            firstT >= 1 - endpointEpsilon ||
            secondT <= endpointEpsilon ||
            secondT >= 1 - endpointEpsilon
          ) {
            continue;
          }

          const distance = Math.hypot(localPoint.x - intersection.point.x, localPoint.y - intersection.point.y);
          if (distance > tolerance || (closest && distance >= closest.distance)) {
            continue;
          }

          closest = {
            point: intersection.point,
            edges: [
              { edgeId: first.edge.id, t: firstT },
              { edgeId: second.edge.id, t: secondT },
            ],
            distance,
          };
        }
      }
    }
  }

  if (!closest) {
    return undefined;
  }

  return { point: closest.point, edges: closest.edges };
}

export function bentEdgeHandles(
  edge: {
    from: { x: number; y: number };
    to: { x: number; y: number };
    fromHandle?: { x: number; y: number };
    toHandle?: { x: number; y: number };
  },
  dx: number,
  dy: number,
): { fromHandle: { x: number; y: number }; toHandle: { x: number; y: number } } {
  const oneThird = lerpPoint(edge.from, edge.to, 1 / 3);
  const twoThirds = lerpPoint(edge.from, edge.to, 2 / 3);
  return {
    fromHandle: {
      x: (edge.fromHandle?.x ?? oneThird.x) + dx,
      y: (edge.fromHandle?.y ?? oneThird.y) + dy,
    },
    toHandle: {
      x: (edge.toHandle?.x ?? twoThirds.x) + dx,
      y: (edge.toHandle?.y ?? twoThirds.y) + dy,
    },
  };
}

export function previewPointForEdge(activeTool: ActiveTool, edge: VectorEdgeHit): { x: number; y: number } | undefined {
  if (activeTool === "path") {
    return edge.point;
  }

  if (activeTool !== "select") {
    return undefined;
  }

  if (!edge.fromHandle && !edge.toHandle) {
    return lerpPoint(edge.from, edge.to, 0.5);
  }

  return undefined;
}

export function insertionForEdge(
  activeTool: ActiveTool,
  edge: VectorEdgeHit,
  point: { x: number; y: number },
  zoom: number,
): { point: { x: number; y: number }; t: number } | undefined {
  if (activeTool === "path") {
    return { point: edge.point, t: edge.t };
  }

  if (edge.fromHandle || edge.toHandle) {
    return undefined;
  }

  const midpoint = lerpPoint(edge.from, edge.to, 0.5);
  return Math.hypot(point.x - midpoint.x, point.y - midpoint.y) <= 9 / zoom
    ? { point: midpoint, t: 0.5 }
    : undefined;
}

function lerpPoint(from: { x: number; y: number }, to: { x: number; y: number }, t: number): { x: number; y: number } {
  return {
    x: from.x + (to.x - from.x) * t,
    y: from.y + (to.y - from.y) * t,
  };
}

function closestPointOnPolyline(
  point: { x: number; y: number },
  points: Array<{ x: number; y: number }>,
): { point: { x: number; y: number }; distance: number; t: number } {
  let closest = { point: points[0] ?? point, distance: Number.POSITIVE_INFINITY, t: 0 };
  for (let index = 0; index < points.length - 1; index += 1) {
    const segment = closestPointOnSegment(point, points[index]!, points[index + 1]!);
    const globalT = (index + segment.t) / Math.max(1, points.length - 1);
    if (segment.distance < closest.distance) {
      closest = { point: segment.point, distance: segment.distance, t: globalT };
    }
  }

  return closest;
}

function closestPointOnSegment(
  point: { x: number; y: number },
  from: { x: number; y: number },
  to: { x: number; y: number },
): { point: { x: number; y: number }; distance: number; t: number } {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const lengthSquared = dx * dx + dy * dy;
  const t = lengthSquared <= 0 ? 0 : Math.max(0, Math.min(1, ((point.x - from.x) * dx + (point.y - from.y) * dy) / lengthSquared));
  const projection = { x: from.x + t * dx, y: from.y + t * dy };
  return {
    point: projection,
    distance: Math.hypot(point.x - projection.x, point.y - projection.y),
    t,
  };
}

function segmentIntersection(
  firstFrom: { x: number; y: number },
  firstTo: { x: number; y: number },
  secondFrom: { x: number; y: number },
  secondTo: { x: number; y: number },
): { point: { x: number; y: number }; firstT: number; secondT: number } | undefined {
  const firstDx = firstTo.x - firstFrom.x;
  const firstDy = firstTo.y - firstFrom.y;
  const secondDx = secondTo.x - secondFrom.x;
  const secondDy = secondTo.y - secondFrom.y;
  const denominator = cross(firstDx, firstDy, secondDx, secondDy);
  if (Math.abs(denominator) <= 1e-9) {
    return undefined;
  }

  const deltaX = secondFrom.x - firstFrom.x;
  const deltaY = secondFrom.y - firstFrom.y;
  const firstT = cross(deltaX, deltaY, secondDx, secondDy) / denominator;
  const secondT = cross(deltaX, deltaY, firstDx, firstDy) / denominator;
  if (firstT < 0 || firstT > 1 || secondT < 0 || secondT > 1) {
    return undefined;
  }

  return {
    point: { x: firstFrom.x + firstT * firstDx, y: firstFrom.y + firstT * firstDy },
    firstT,
    secondT,
  };
}

function pointsBounds(points: Array<{ x: number; y: number }>): Rect {
  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  for (const point of points) {
    minX = Math.min(minX, point.x);
    minY = Math.min(minY, point.y);
    maxX = Math.max(maxX, point.x);
    maxY = Math.max(maxY, point.y);
  }

  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}

function pointInExpandedSegmentBounds(
  point: { x: number; y: number },
  from: { x: number; y: number },
  to: { x: number; y: number },
  padding: number,
): boolean {
  return (
    point.x >= Math.min(from.x, to.x) - padding &&
    point.x <= Math.max(from.x, to.x) + padding &&
    point.y >= Math.min(from.y, to.y) - padding &&
    point.y <= Math.max(from.y, to.y) + padding
  );
}

function pointInExpandedBounds(point: { x: number; y: number }, bounds: Rect, padding: number): boolean {
  return (
    point.x >= bounds.x - padding &&
    point.x <= bounds.x + bounds.width + padding &&
    point.y >= bounds.y - padding &&
    point.y <= bounds.y + bounds.height + padding
  );
}

function cross(ax: number, ay: number, bx: number, by: number): number {
  return ax * by - ay * bx;
}
